// ─── Trainer Schedule — logika murni halaman (tanpa React) ───────────────────
//
// Semua yang menentukan ISI halaman ada di sini supaya bisa diuji tanpa merender apa
// pun: status trainer di jam terpilih, urutan, penggabungan blok, dan posisinya di
// linimasa.
//
// Bentrok jam TIDAK dihitung di sini. Server mengirim daftar `bentrok` tiap kelas
// (trainerschedule.controller), jadi aturan "dua sesi bentrok bila beririsan" hanya
// hidup di satu tempat. `bentrok: null` = jam kelasnya tak terbaca.

export const HARI_SINGKAT = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
export const HARI_PANJANG = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
// Pilihan hari di halaman = Senin–Sabtu. Tak memilih hari = keenamnya.
export const HARI_KERJA = [1, 2, 3, 4, 5, 6];
export const NAMA_BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli",
  "Agustus", "September", "Oktober", "November", "Desember"];
const BULAN_SINGKAT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

// Rentang linimasa: 08.00–22.00, dalam menit sejak 00.00.
export const AWAL = 8 * 60;
export const AKHIR = 22 * 60;

const pad2 = (n) => String(n).padStart(2, "0");
export const jamTeks = (m) => `${pad2(Math.floor(m / 60))}.${pad2(m % 60)}`;
export const rentangTeks = (mulai, selesai) => `${jamTeks(mulai)}–${jamTeks(selesai)}`;
// "18.45 - 20.15" (teks sesi apa adanya) → "18.45–20.15" untuk dibaca.
export const sesiTeks = (s) => String(s || "").trim().replace(/\s*-\s*/, "–");

export function periode(mulai) {
  if (mulai < 12 * 60) return "Pagi";
  if (mulai < 16 * 60) return "Siang";
  if (mulai < 18 * 60) return "Sore";
  return "Malam";
}

/** Hari terpilih untuk dibaca: [] → "Senin–Sabtu"; [2, 4] → "Sel, Kam". */
export const hariTeks = (hari) => (hari.length
  ? [...hari].sort((a, b) => a - b).map((d) => HARI_SINGKAT[d]).join(", ")
  : "Senin–Sabtu");

// ── Status di jam terpilih ──────────────────────────────────────────────────
export const STATUS_LABEL = { kosong: "Jam kosong", "belum-pasti": "Belum pasti", "ada-kelas": "Ada kelas" };
const STATUS_URUT = { kosong: 0, "belum-pasti": 1, "ada-kelas": 2 };

/**
 * Status trainer pada jam `sesi` (label tombol jam) dan hari `hari` ([] = Senin–Sabtu):
 *   "ada-kelas"   — punya kelas yang harinya terisi, jamnya bentrok, dan jalan di salah satu
 *                   hari terpilih
 *   "belum-pasti" — selain itu, punya kelas yang jamnya bentrok tapi harinya belum diisi
 *                   admin; atau kelas yang jamnya tak terbaca sehingga tak bisa dipastikan
 *   "kosong"      — selain itu. Jam yang bertumpuk sudah dihitung bentrok oleh server, jadi
 *                   "kosong" berarti benar-benar bisa dipakai.
 */
export function statusTrainer(trainer, sesi, hari = []) {
  const H = hari.length ? hari : HARI_KERJA;
  const kenaHari = (k) => k.hari.some((d) => H.includes(d));
  let belumPasti = false;
  for (const k of trainer.kelas) {
    if (k.bentrok === null) {
      if (!k.hariDiisi || kenaHari(k)) belumPasti = true;
    } else if (k.bentrok.includes(sesi)) {
      if (!k.hariDiisi) belumPasti = true;
      else if (kenaHari(k)) return "ada-kelas";
    }
  }
  return belumPasti ? "belum-pasti" : "kosong";
}

export function hitungStatus(trainers, sesi, hari = []) {
  const n = { kosong: 0, "belum-pasti": 0, "ada-kelas": 0 };
  trainers.forEach((t) => { n[statusTrainer(t, sesi, hari)] += 1; });
  return n;
}

// ── Penyaring & urutan ──────────────────────────────────────────────────────
export function saring(trainers, { cari = "", cabang = "" } = {}) {
  const q = cari.trim().toLowerCase();
  return trainers.filter((t) =>
    (!q || t.nama.toLowerCase().includes(q) || t.kode.toLowerCase().includes(q))
    && (!cabang || t.cabang.includes(cabang) || t.kelas.some((k) => k.cabang === cabang)));
}

/**
 * Menit mengajar per minggu menurut jadwal: durasi × banyaknya hari ia mengajar kelas itu.
 * Kelas yang harinya belum diisi dihitung sekali seminggu (paling sedikit sekali ia pasti
 * jalan); kelas yang jamnya tak terbaca tak bisa dihitung.
 */
export function menitSeminggu(trainer) {
  return trainer.kelas.reduce((n, k) => (k.mulai == null || k.selesai == null
    ? n
    : n + (k.selesai - k.mulai) * (k.hariDiisi ? k.hari.length : 1)), 0);
}

export const URUTAN = [
  ["nama", "Nama A–Z"],
  ["jam-sedikit", "Paling sedikit jam mengajar"],
  ["kelas-banyak", "Paling banyak kelas"],
  ["mengganti", "Paling sering mengganti trainer lain"],
  ["diganti", "Paling sering kelasnya diganti"]
];

// "Mr. Andi" diurutkan sebagai "Andi": sapaan bukan bagian nama yang dicari orang.
const SAPAAN = /^(mr|mrs|ms|miss|mx|pak|bu|ibu|bapak|kak|sir)\.?$/i;
const kataNama = (nama) => String(nama || "").replace(/\./g, ". ").split(/\s+/).filter(Boolean);
const tanpaSapaan = (nama) => {
  const kata = kataNama(nama);
  const inti = kata.filter((k) => !SAPAAN.test(k));
  return (inti.length ? inti : kata).join(" ");
};
const bandingNama = (a, b) => tanpaSapaan(a.nama).localeCompare(tanpaSapaan(b.nama), "id", { sensitivity: "base" })
  || a.nama.localeCompare(b.nama, "id") || a.id - b.id;

const KUNCI_URUTAN = {
  nama: () => 0,
  "jam-sedikit": (t) => menitSeminggu(t),
  "kelas-banyak": (t) => -t.kelas.length,
  mengganti: (t) => -t.bulanIni.mengganti.kali,
  diganti: (t) => -t.bulanIni.diganti.kali
};

/** Saat jam dipilih: Jam kosong → Belum pasti → Ada kelas, baru urutan pilihan; seri → nama. */
export function urutkan(trainers, urutan = "nama", sesi = "", hari = []) {
  const kunci = KUNCI_URUTAN[urutan] || KUNCI_URUTAN.nama;
  const status = sesi
    ? new Map(trainers.map((t) => [t.id, STATUS_URUT[statusTrainer(t, sesi, hari)]]))
    : null;
  return [...trainers].sort((a, b) => (status ? status.get(a.id) - status.get(b.id) : 0)
    || kunci(a) - kunci(b) || bandingNama(a, b));
}

// ── Linimasa ────────────────────────────────────────────────────────────────
/**
 * Kelas seorang trainer → blok linimasa. Jam yang sama, di cabang mana pun, jadi SATU blok
 * ("BR01 · BR04"). Blok yang bertumpuk (18.45–20.15 dan 19.00–20.30 di hari berbeda)
 * diberi lajur sendiri supaya tak saling menutupi. Kelas yang jamnya tak terbaca tak bisa
 * diletakkan — dikembalikan terpisah sebagai `takTerbaca`.
 */
export function blokTrainer(trainer) {
  const peta = new Map();
  trainer.kelas.forEach((k) => {
    if (k.mulai == null || k.selesai == null) return;
    const kunci = `${k.mulai}-${k.selesai}`;
    if (!peta.has(kunci)) peta.set(kunci, { kunci, mulai: k.mulai, selesai: k.selesai, kelas: [] });
    peta.get(kunci).kelas.push(k);
  });
  const blok = [...peta.values()]
    .sort((a, b) => a.mulai - b.mulai || a.selesai - b.selesai)
    .map((b) => ({
      ...b,
      cabang: [...new Set(b.kelas.map((k) => k.cabang))].sort(),
      // Kuning hanya bila SEMUA kelasnya belum berhari; campuran tetap biru + "hari?".
      tanpaHari: b.kelas.every((k) => !k.hariDiisi),
      adaTanpaHari: b.kelas.some((k) => !k.hariDiisi)
    }));
  const akhirLajur = [];
  blok.forEach((b) => {
    let i = akhirLajur.findIndex((akhir) => akhir <= b.mulai);
    if (i === -1) { i = akhirLajur.length; akhirLajur.push(0); }
    akhirLajur[i] = b.selesai;
    b.lajur = i;
  });
  return {
    blok,
    lajur: Math.max(1, akhirLajur.length),
    takTerbaca: trainer.kelas.filter((k) => k.mulai == null || k.selesai == null)
  };
}

/** Posisi rentang jam di linimasa, dalam persen lebar jalur jam (dipotong ke 08.00–22.00). */
export function posisi(mulai, selesai) {
  const jepit = (m) => Math.max(AWAL, Math.min(AKHIR, m));
  const a = jepit(mulai);
  const b = jepit(selesai);
  return { left: ((a - AWAL) / (AKHIR - AWAL)) * 100, width: ((b - a) / (AKHIR - AWAL)) * 100 };
}

/** Sesi ≥ 90 menit cukup lebar untuk "16.00–17.30"; yang lebih pendek cukup jam mulainya. */
export const labelBlok = (mulai, selesai) => (selesai - mulai >= 90 ? rentangTeks(mulai, selesai) : jamTeks(mulai));

export function inisial(nama) {
  const kata = kataNama(nama);
  const inti = kata.filter((k) => !SAPAAN.test(k));
  return (inti.length ? inti : kata).slice(0, 2).map((k) => k[0]).join("").toUpperCase() || "?";
}

// ── Tanggal ─────────────────────────────────────────────────────────────────
const keTanggal = (iso) => { const [y, m, d] = String(iso).split("-").map(Number); return new Date(y, m - 1, d); };
export const tanggalTeks = (iso) => {
  const t = keTanggal(iso);
  return `${HARI_SINGKAT[t.getDay()]}, ${t.getDate()} ${BULAN_SINGKAT[t.getMonth()]}`;
};

/** Minggu-minggu sebulan, mulai SENIN. Sel di luar bulan = null. */
export function kalender(tahun, bulan) {
  const jumlah = new Date(tahun, bulan, 0).getDate();
  const geser = (new Date(tahun, bulan - 1, 1).getDay() + 6) % 7;
  const sel = [...Array(geser).fill(null), ...Array.from({ length: jumlah }, (_, i) => i + 1)];
  while (sel.length % 7) sel.push(null);
  const minggu = [];
  for (let i = 0; i < sel.length; i += 7) minggu.push(sel.slice(i, i + 7));
  return minggu;
}
